import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { GraduationCap, Briefcase, Award, Utensils, Video, BookOpen, MapPin, Clock, Users, ArrowRight, CheckCircle, X, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_CONFIG = {
  job_readiness: { label: "Job Readiness", icon: Briefcase, color: "bg-warm-100 text-warm-800" },
  education: { label: "Education Classes", icon: BookOpen, color: "bg-blue-50 text-blue-800" },
  hospitality: { label: "Hospitality", icon: Utensils, color: "bg-amber-50 text-amber-800" },
  certifications: { label: "Certifications", icon: Award, color: "bg-sage-100 text-sage-800" },
  content_creation: { label: "Content Creation", icon: Video, color: "bg-purple-50 text-purple-800" },
};

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/services`)
      .then((res) => setServices(res.data))
      .catch(() => toast.error("Failed to load services"))
      .finally(() => setLoading(false));
  }, []);

  const handleEnroll = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/services/enroll`, { service_id: enrolling.id, ...form });
      toast.success("Enrollment submitted! We'll contact you shortly.");
      setEnrolling(null);
      setForm({ name: "", email: "", phone: "", notes: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Enrollment failed");
    } finally {
      setSubmitting(false);
    }
  };

  const grouped = {};
  services.forEach((s) => {
    if (!grouped[s.category]) grouped[s.category] = [];
    grouped[s.category].push(s);
  });

  return (
    <div>
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-warm-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-warm-600 mb-4 block">
              Survivor Services
            </span>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-tight text-gray-900 mb-6"
              style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}
              data-testid="services-page-title"
            >
              Build Your Future
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl">
              We partner with local businesses and education providers to offer free job training,
              certifications, and career services to help survivors get on their feet and thrive.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Icons */}
      <section className="py-12 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-6">
            {Object.entries(CAT_CONFIG).map(([key, cfg]) => (
              <a
                key={key}
                href={`#cat-${key}`}
                className="flex flex-col items-center gap-2 px-4 py-3 rounded-xl hover:bg-warm-50 transition-colors"
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${cfg.color}`}>
                  <cfg.icon className="w-5 h-5" />
                </div>
                <span className="text-xs font-medium text-gray-600">{cfg.label}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-warm-500" />
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-20">
              <GraduationCap className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-medium text-gray-600" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                Programs Coming Soon
              </h3>
              <p className="text-gray-500 mt-2">We're building partnerships to bring you job training and education opportunities.</p>
            </div>
          ) : (
            <div className="space-y-16">
              {Object.entries(grouped).map(([category, items]) => {
                const cfg = CAT_CONFIG[category] || { label: category, icon: BookOpen, color: "bg-gray-100 text-gray-800" };
                return (
                  <div key={category} id={`cat-${category}`}>
                    <div className="flex items-center gap-3 mb-6">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${cfg.color}`}>
                        <cfg.icon className="w-5 h-5" />
                      </div>
                      <h2
                        className="text-2xl sm:text-3xl font-medium text-gray-900"
                        style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}
                      >
                        {cfg.label}
                      </h2>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {items.map((s, idx) => (
                        <motion.div
                          key={s.id}
                          initial={{ opacity: 0, y: 15 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: idx * 0.05 }}
                          className="bg-white border border-gray-100 rounded-2xl p-6 hover:shadow-lg transition-shadow"
                          data-testid={`service-card-${s.id}`}
                        >
                          <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                            {s.title}
                          </h3>
                          <p className="text-sm text-gray-600 mb-4 leading-relaxed">{s.description}</p>

                          <div className="space-y-2 text-sm text-gray-500 mb-4">
                            {s.vendor_name && (
                              <div className="flex items-center gap-2">
                                <Users className="w-4 h-4 text-warm-400" />
                                <span>Partner: {s.vendor_name}</span>
                              </div>
                            )}
                            {s.schedule && (
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-warm-400" />
                                <span>{s.schedule}{s.duration ? ` (${s.duration})` : ""}</span>
                              </div>
                            )}
                            {s.location && (
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-warm-400" />
                                <span>{s.location}</span>
                              </div>
                            )}
                            {s.certification_offered && (
                              <div className="flex items-center gap-2">
                                <Award className="w-4 h-4 text-sage-500" />
                                <span className="text-sage-700 font-medium">{s.certification_offered}</span>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-warm-600">{s.cost || "Free"}</span>
                            <Button
                              size="sm"
                              className="bg-warm-700 hover:bg-warm-800 text-white rounded-full px-5 gap-1"
                              onClick={() => setEnrolling(s)}
                              data-testid={`enroll-btn-${s.id}`}
                            >
                              Enroll <ArrowRight className="w-3 h-3" />
                            </Button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Enrollment Modal */}
      {enrolling && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-md p-6"
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                Enroll: {enrolling.title}
              </h3>
              <button onClick={() => setEnrolling(null)} data-testid="close-enroll-modal"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleEnroll} className="space-y-4" data-testid="service-enroll-form">
              <div>
                <label className="text-sm font-medium text-gray-700">Full Name *</label>
                <input className="w-full border rounded-lg px-3 py-2 mt-1" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Email *</label>
                <input type="email" className="w-full border rounded-lg px-3 py-2 mt-1" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Phone</label>
                <input className="w-full border rounded-lg px-3 py-2 mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Notes</label>
                <textarea className="w-full border rounded-lg px-3 py-2 mt-1" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any questions or special needs" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-warm-700 hover:bg-warm-800 text-white rounded-full" data-testid="submit-enrollment-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                Submit Enrollment
              </Button>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ServicesPage;
